// ─── The multi-page site model ────────────────────────────────────────────────
// A generated site is a list of pages that share one stylesheet and one script:
//
//   [{ path: 'index.html', html: '…' }, { path: 'about.html', html: '…' }]
//
// Everything downstream — the preview frame, the code tab, ZIP export, the
// public preview — reads that shape through this module, so a site coming off
// the stream, out of the database, or out of a stored version all behave alike.

export const DEFAULT_PAGE = 'index.html';

/**
 * Coerce a page name or link target into a safe, relative .html filename.
 * Mirrors normalizePagePath in builderslab/server/services/generator.js: a link
 * navigates only when its normalised target matches a normalised page path, so
 * the two must agree.
 */
export function normalizePagePath(raw) {
  let p = String(raw || '').trim().toLowerCase();
  p = p.split(/[?#]/)[0];                    // drop query string / fragment
  p = p.replace(/^[a-z]+:\/\/[^/]*/, '');    // absolute URL → path
  p = p.replace(/\\/g, '/');
  p = p.replace(/^[./]+/, '');               // leading ./ or /
  p = p.replace(/[^a-z0-9\-_/.]/g, '-');     // keep the charset boring
  p = p.replace(/\/+/g, '/');
  // "blog/" means the directory index — resolve it before the segment filter
  // below strips the empty trailing segment and turns it into "blog.html".
  const isDir = p.endsWith('/');
  // Refuse traversal outright rather than trying to resolve it.
  p = p.split('/').filter(seg => seg && seg !== '.' && seg !== '..').join('/');
  if (!p) return DEFAULT_PAGE;
  if (isDir) return `${p}/${DEFAULT_PAGE}`;
  if (/\.html?$/.test(p)) return p.replace(/\.htm$/, '.html');
  return `${p}.html`;
}


/**
 * Trailing junk after the last section, removed.
 *
 * ===JS=== runs to the end of the response, so anything the model emits after
 * its final line lands inside the script. A live site shipped with a stray
 * `</arg_value>` on the last line — a tool-call artifact the model wrote after
 * finishing — and that one token made the whole file a syntax error. The markup
 * rendered perfectly and NOTHING worked: no nav, no filters, no cart, no forms.
 *
 * Only a line that is entirely an XML-ish tag is dropped, and only from the
 * end. Valid JavaScript never has one, so this cannot eat real code.
 */
export function stripTrailingArtifacts(code) {
  let out = String(code || '').replace(/\s+$/, '');
  let prev;
  do {
    prev = out;
    out = out.replace(/\n[ \t]*<\/?[A-Za-z_][\w:.-]*\s*\/?>[ \t]*$/, '');
  } while (out !== prev);
  return out;
}

/**
 * Normalise any of the shapes a site arrives in into an ordered page list.
 * Accepts `{ pages }`, `{ html }`, or a database row with `html_content`.
 * index.html always leads, so the first entry is the page to open.
 */
export function toPages(site) {
  if (!site) return [];

  // `pages` is a MySQL JSON column. The driver normally hands back a parsed
  // array, but not always — services/sitePages.js on the server has tolerated a
  // raw string since it was written, and this did not. A string fell through to
  // the html_content fallback below, so a four-page site became a one-page one:
  // no page tabs, and every link in the nav resolving to a page the workspace
  // did not think existed, which the frame then blocked. The published site was
  // fine the whole time, because that route normalises before responding.
  let raw = site.pages;
  if (typeof raw === 'string') {
    try { raw = JSON.parse(raw); } catch { raw = null; }
  }
  if (!Array.isArray(raw)) raw = [];
  const cleaned = raw
    .filter(p => p && typeof p.html === 'string')
    .map(p => ({ path: normalizePagePath(p.path), html: p.html }));

  // Deduplicate on path, last write wins, order otherwise preserved.
  const byPath = new Map();
  cleaned.forEach(p => byPath.set(p.path, p));
  let pages = [...byPath.values()];

  if (!pages.length) {
    const home = site.html ?? site.html_content ?? '';
    if (!home) return [];
    pages = [{ path: DEFAULT_PAGE, html: home }];
  }

  return pages.sort((a, b) =>
    a.path === DEFAULT_PAGE ? -1 : b.path === DEFAULT_PAGE ? 1 : 0);
}

/** The page a preview should open on. */
export function homePath(pages) {
  return pages.find(p => p.path === DEFAULT_PAGE)?.path ?? pages[0]?.path ?? DEFAULT_PAGE;
}

export function findPage(pages, path) {
  return pages.find(p => p.path === path) ?? pages[0] ?? null;
}

/** "about.html" → "About", for tabs and file labels. */
export function pageLabel(path) {
  const base = String(path || '').replace(/\.html$/, '').split('/').pop() || 'index';
  if (base === 'index') return 'Home';
  return base
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

// ─── Link resolution ──────────────────────────────────────────────────────────

/**
 * Decide what an anchor inside the preview should do.
 *
 * This is the fix for links dumping the visitor on BuildersLab: the preview
 * frame has no real origin of its own, so a relative href like "about.html"
 * resolves against builderslab.net and navigates the frame straight out of the
 * generated site. Nothing may navigate the frame — every link is classified
 * here and handled by the caller instead.
 *
 * @returns {{kind:'page', path:string}          switch the preview to that page
 *        |  {kind:'hash', hash:string}          scroll within the current page
 *        |  {kind:'external', href:string}      open in a new tab
 *        |  {kind:'ignore'}                     mailto:/tel:/#/blank — do nothing
 *        |  {kind:'missing', href:string}}      a link to a page that was never
 *                                               generated; blocked, never followed
 */
export function resolveLink(href, pages, currentPath = DEFAULT_PAGE) {
  const raw = String(href ?? '').trim();

  if (!raw || raw === '#' || /^javascript:/i.test(raw)) return { kind: 'ignore' };
  if (/^(mailto:|tel:|sms:)/i.test(raw)) return { kind: 'ignore' };

  // Same-page anchor.
  if (raw.startsWith('#')) return { kind: 'hash', hash: raw };

  const isAbsolute = /^[a-z]+:\/\//i.test(raw) || raw.startsWith('//');
  const known = new Set(pages.map(p => p.path));

  if (isAbsolute) {
    // An absolute URL that happens to point at one of our own filenames is
    // still an internal link — models write these when they get carried away.
    const candidate = normalizePagePath(raw);
    if (known.has(candidate)) return { kind: 'page', path: candidate, hash: hashOf(raw) };
    return { kind: 'external', href: raw };
  }

  const candidate = normalizePagePath(raw);
  if (known.has(candidate)) {
    // A link to the page you are already on with a hash is just a scroll.
    if (candidate === currentPath && hashOf(raw)) return { kind: 'hash', hash: hashOf(raw) };
    return { kind: 'page', path: candidate, hash: hashOf(raw) };
  }

  return { kind: 'missing', href: raw };
}

function hashOf(href) {
  const i = String(href).indexOf('#');
  return i === -1 ? '' : String(href).slice(i);
}

// ─── Export ───────────────────────────────────────────────────────────────────

/**
 * The icon with its SVG namespace, which it needs everywhere it is used.
 *
 * Inline in a page an <svg> works without xmlns, so the model usually leaves it
 * off. But the icon is only ever shown as an image — a favicon, favicon.svg, an
 * <img> in the workspace and dashboard — and an SVG image without its
 * namespace is not SVG to the browser: it drew a broken-image "?" in the
 * workspace, the dashboard and the browser tab. The server now adds it when it
 * saves an icon; this covers every site saved before that.
 */
export function withSvgNamespace(icon) {
  const svg = String(icon || '').trim();
  const open = svg.match(/^<svg\b[^>]*>/i);
  if (!open || /\sxmlns\s*=/.test(open[0])) return svg;
  return svg.replace(/^<svg\b/i, '<svg xmlns="http://www.w3.org/2000/svg"');
}

/**
 * The generated icon as a data: URI, for use as a favicon `href` or an <img src>.
 * Returns '' when there is no icon, so callers can fall back cleanly.
 */
export function iconDataUri(icon) {
  const svg = withSvgNamespace(icon);
  if (!svg.startsWith('<svg')) return '';
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/** A standalone page document, linking the shared stylesheet, script and icon. */
export function buildPageDoc({ title, path, html, icon, cssHref = 'style.css', jsSrc = 'script.js', iconHref = 'favicon.svg' }) {
  const favicon = icon ? `\n  <link rel="icon" type="image/svg+xml" href="${iconHref}">` : '';
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapeHtml(title || pageLabel(path))}</title>${favicon}
  <script src="https://cdn.tailwindcss.com"></script>
  <link rel="stylesheet" href="${cssHref}">
</head>
<body>
${html || ''}
<script src="${jsSrc}"></script>
</body>
</html>`;
}

/** A self-contained single document — for the "copy full page" affordance. */
export function buildStandaloneDoc({ title, html, css, js } = {}) {
  return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>${escapeHtml(title || 'Website')}</title><script src="https://cdn.tailwindcss.com"></script><style>${css || ''}</style></head><body style="background:#ffffff;margin:0;">${html || ''}<script>${js || ''}</script></body></html>`;
}

/**
 * The complete file listing for a downloadable site: every page plus the shared
 * assets. Used by every ZIP export so they cannot drift apart.
 * @returns {Array<{name:string, content:string}>}
 */
/**
 * Move the generator's flat links to wherever the file actually ended up.
 *
 * The generator is told to write plain relative filenames — `<a href="about.html">`
 * — and that is correct for a flat folder. The export is not flat any more: the
 * home page stays at the root and the rest go under `pages/`, which is what the
 * code panel shows. Without this pass every link in the downloaded site is broken,
 * so the tree would be describing a structure the ZIP does not have.
 *
 * Only hrefs that name a page this site actually emits are touched. Anything else
 * — an external URL, an in-page `#anchor`, a link to a page that was never built —
 * is left exactly as written.
 */
export function rewriteInternalLinks(html, { inPagesFolder, pageNames }) {
  if (!html) return html || '';
  const names = new Set(pageNames);

  return String(html).replace(
    /(\shref=)(["'])([^"'#?]+)([^"']*)\2/gi,
    (whole, attr, quote, target, suffix) => {
      if (!names.has(target)) return whole;

      const moved = target === DEFAULT_PAGE
        // Home stayed at the root.
        ? (inPagesFolder ? `../${DEFAULT_PAGE}` : DEFAULT_PAGE)
        // Everything else lives in pages/ — a sibling link needs no prefix.
        : (inPagesFolder ? target : `pages/${target}`);

      return `${attr}${quote}${moved}${suffix}${quote}`;
    },
  );
}

/**
 * The site as the Code tab shows it: saved code with any unsaved edits laid
 * over it. `drafts` is keyed the way the file tree keys files — `html` for the
 * home page, the filename for every other page, then `css`, `js` and `icon`.
 *
 * Shared by the save and the download. The download used to read the saved
 * code alone, so one taken inside the autosave pause left out the edit that
 * had just been typed.
 */
export function withDrafts(site, drafts = {}) {
  const has = (key) => drafts[key] !== undefined;
  const pages = toPages(site).map(p => {
    const key = p.path === DEFAULT_PAGE ? 'html' : p.path;
    return { path: p.path, html: has(key) ? drafts[key] : p.html };
  });
  return {
    ...site,
    pages,
    html: has('html') ? drafts.html : site?.html,
    css:  has('css')  ? drafts.css  : site?.css,
    js:   has('js')   ? drafts.js   : site?.js,
    icon: has('icon') ? drafts.icon : site?.icon,
  };
}

/**
 * A file-system-safe name for a site's download. It only replaced whitespace
 * before, so "DOVERA EMPIRE — Official Site" downloaded as
 * `dovera-empire-—-official-site.zip`. Accents are folded to their letters; a
 * title with nothing Latin left in it falls back to "website".
 */
export function siteSlug(title) {
  const slug = String(title || '')
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .slice(0, 60)
    .replace(/^-+|-+$/g, '');
  return slug || 'website';
}

export function buildSiteFiles(site) {
  const pages = toPages(site);
  const title = site?.title || 'Website';
  const icon  = site?.icon ?? site?.icon_svg ?? '';
  const pageNames = pages.map(p => p.path);

  // Mirrors the code panel: index.html at the root, other pages under pages/,
  // shared assets under assets/.
  const files = pages.map(p => {
    const inPagesFolder = p.path !== DEFAULT_PAGE;
    const up = inPagesFolder ? '../' : '';
    return {
      name: inPagesFolder ? `pages/${p.path}` : p.path,
      content: buildPageDoc({
        title,
        path: p.path,
        html: rewriteInternalLinks(p.html, { inPagesFolder, pageNames }),
        icon,
        cssHref:  `${up}assets/style.css`,
        jsSrc:    `${up}assets/script.js`,
        iconHref: `${up}assets/favicon.svg`,
      }),
    };
  });

  files.push({ name: 'assets/style.css', content: site?.css ?? site?.css_content ?? '' });
  files.push({ name: 'assets/script.js', content: site?.js ?? site?.js_content ?? '' });
  // Shipped as a real file so the exported site has a working favicon offline,
  // with no data: URI bloating every page.
  if (icon) files.push({ name: 'assets/favicon.svg', content: withSvgNamespace(icon) });

  return files;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"]/g, c => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
  ));
}
